import type { DropdownOptionFilter, FilterConfig, Maybe, TraffickingFilters } from '../../../apis/graphql';
import {
  FilterInputType,
  FilterOperator,
  RelativeDirection,
  removeTypeNamesFromObject,
  StringOperator,
} from '../../../apis/graphql';
import type { DropdownOption } from '../../../common/Dropdown';
import { PredefinedCustomFilterId } from '../../../configs/customFilters';
import { TraffickingTableName } from '../../../constants';
import { TRAFFICKING_SEARCH_PARAMS, TRAFFICKING_TABLE_NAMES } from '../../../constants';
import type { Nullable } from '../../../models';
import { clampRelativeValue } from '../../../pages/Trafficking/FilterPanel/FilterRow/DateControl/relativeDateBounds';
import { todayLocalIso } from '../../../pages/Trafficking/FilterPanel/FilterRow/DateControl/relativeDateRange';
import { ALL_TIME_DATE_KEY } from '../../../pages/Trafficking/TableFilterBar/DateFilter';
import type { SelectedDateValue } from '../../../pages/Trafficking/TableFilterBar/DateFilter/helpers';
import { workWithSessionStorage } from '../../../utils/storage/sessionStorage';
import { StorageEntity } from '../../../utils/storage/storageEntity';
import {
  INITIAL_FILTERS,
  INITIAL_TABLE_FILTERS,
  LINE_ITEMS_STATUSES,
  TRAFFICKING_FILTERS_SEARCH_PARAMS,
  TRAFFICKING_INITIAL_TABLE_FILTERS,
} from './constants';
import type {
  GlobalFilters,
  LineItemStatusFiltersInput,
  OmittedCustomFiltersFields,
  OperatorFilterValue,
  OverriddenCustomPacingEligibleFields,
  TableFilters,
} from './types';

export type { OperatorFilterValue };

// What's actually persisted to session storage — GlobalFilters (flat arrays for the 15
// KATANA-1499 fields), not TraffickingFilters, since everything written here (useFilters.ts,
// setFilterDataToStorage below) is the GlobalFilters shape. Two fields need widening past
// Partial<GlobalFilters>: lineItemSubStatusList is written directly by setFilterDataToStorage
// even though it's not a GlobalFilters key (folded into lineItemStatusList on read), and dateFilter
// tolerates a raw `null` from older stored payloads. See getDefaultFilters for the read side, which
// is additionally tolerant of a raw TraffickingFilters response (StringOperatorFilter for the 15
// fields) coming from a shareable link or custom filter.
type PersistedFilters = Omit<Partial<GlobalFilters>, 'dateFilter'> & {
  dateFilter?: Maybe<SelectedDateValue>;
  lineItemSubStatusList?: Maybe<Array<string>>;
};

export const filtersData = new StorageEntity<PersistedFilters>('filters', workWithSessionStorage);
export const customFilterData = new StorageEntity<Nullable<DropdownOption<TraffickingFilters>>>(
  'customFilter',
  workWithSessionStorage
);

const mergeTableFilters = <T extends keyof typeof TraffickingTableName>(
  tableName: T,
  tableFilters?: TableFilters[T]
): TableFilters[T] => {
  if (!tableFilters) return INITIAL_TABLE_FILTERS[tableName];

  return {
    selectedRowIds: tableFilters.selectedRowIds?.filter(Boolean) || INITIAL_TABLE_FILTERS[tableName].selectedRowIds,
    searchTerm: tableFilters.searchTerm || INITIAL_TABLE_FILTERS[tableName].searchTerm,
    sortBy: tableFilters.sortBy || INITIAL_TABLE_FILTERS[tableName].sortBy,
  } as TableFilters[T];
};

export const sortLineItemStatusesAndSubStatuses = (
  lineItemStatusListFilter: string[]
): {
  filteredStatuses: string[];
  filteredSubStatuses: string[];
} => {
  const lineItemStatuses = Object.values(LINE_ITEMS_STATUSES);
  const filteredStatuses: string[] = [];
  const filteredSubStatuses: string[] = [];

  lineItemStatusListFilter.forEach((filteredStatus) => {
    if (lineItemStatuses.some((status) => status === filteredStatus)) {
      filteredStatuses.push(filteredStatus);
    } else {
      filteredSubStatuses.push(filteredStatus);
    }
  });

  return {
    filteredStatuses,
    filteredSubStatuses,
  };
};

export const getLineItemStatusFiltersInput = (statusList?: string[] | null): LineItemStatusFiltersInput => {
  if (statusList?.length) {
    const { filteredStatuses, filteredSubStatuses } = sortLineItemStatusesAndSubStatuses(statusList);

    return {
      lineItemStatusList: filteredStatuses,
      lineItemSubStatusList: filteredSubStatuses,
    };
  }

  return {};
};

export const mergeLineItemStatusFilters = (
  lineItemStatusList?: string[] | null,
  lineItemSubStatusList?: string[] | null
): Pick<GlobalFilters, 'lineItemStatusList'> => {
  const result: string[] = [];

  if (lineItemStatusList?.length) {
    result.push(...lineItemStatusList);
  }

  if (lineItemSubStatusList?.length) {
    result.push(...lineItemSubStatusList);
  }

  return {
    lineItemStatusList: result.length ? result : INITIAL_FILTERS.lineItemStatusList,
  };
};

const isTraffickingTableFilter = (filter: keyof GlobalFilters): filter is keyof typeof TraffickingTableName =>
  TRAFFICKING_TABLE_NAMES.some((tableName) => tableName === filter);

const isTraffickingDefaultFilter = (filter: keyof GlobalFilters): filter is keyof typeof TraffickingTableName =>
  TRAFFICKING_INITIAL_TABLE_FILTERS.some((defaultFilter) => defaultFilter === filter);

export const isTraffickingLineItemStatusFilter = (filter: keyof GlobalFilters): filter is 'lineItemStatusList' =>
  filter === TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_STATUS_LIST;

export const isTraffickingLineItemStatusFilterValue = (
  filter: keyof GlobalFilters,
  _value: unknown
): _value is GlobalFilters['lineItemStatusList'] => filter === TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_STATUS_LIST;

// KATANA-1499: the 15 always-CONTAINS string/enum filters that are StringOperatorFilter on
// TraffickingFilters but stay bare arrays on GlobalFilters (no operator to preserve for an
// always-CONTAINS dimension — see the StringOperatorFieldsToFlat comment in ./types.ts).
// Canonical home for both directions (load-unwrap here, save-wrap via wrapSaveStringOperatorField)
// — pages/Trafficking/utils.ts and CustomSavedFilters/utils.ts both already depend on this module,
// so importing from here rather than duplicating avoids introducing a new edge back into
// pages/Trafficking/utils.ts that would create a require cycle through common/hooks/config.ts.
//
// agencyIdList/industryIdList (KATANA-1499 follow-up, Option C) are included for the *save*
// direction only: their write-side type (TraffickingFiltersInput) is StringOperatorFilterInput, so
// wrapSaveStringOperatorField must wrap them like the 15 above. The *load* direction
// (unwrapStringOperatorField) needs no bespoke entry here either — a loaded
// TraffickingFilters.agencyIdList is EntityOperatorFilter (`{operator, values: DropdownOptionFilter[]}`),
// which isStringOperatorFilterShape's structural check (object, not array, has `values`) already
// matches, so the generic unwrap extracts `.values` (labels intact) same as the 15 fields above.
//
// This Set is the legacy floor only — see getStringOperatorFilterKeys below for the full
// (floor ∪ config-derived) picture consumers should use when filterConfigs is available.
export const STRING_OPERATOR_FIELDS = new Set([
  'adProductList',
  'generalPacingStatusList',
  'reviewStatusList',
  'orderTypeList',
  'tierNameList',
  'priorityValueList',
  'billableEventList',
  'creativeReviewList',
  'adTypeList',
  'currencyList',
  'countryList',
  'lineItemTypeList',
  'sspList',
  'costMethodList',
  'distributionPartnerList',
  'agencyIdList',
  'industryIdList',
  // KATANA-1590: migrated onto the same StringOperatorFilterInput mechanism as the dims above —
  // architectural parity, still always-CONTAINS (no operator picker for Publisher).
  'publisherList',
]);

// Config-driven overlay on top of the static floor Set above.
// STRING_OPERATOR_FIELDS alone is the fallback when filterConfigs is empty (config still
// loading or errored). When the catalog is loaded, union in every id whose FilterConfig
// declares inputType STRING_OPERATOR — this makes new config-only fields (e.g.
// buyerNameList/omsPackageNameList) work without a hand-edit here.
export const getStringOperatorFilterKeys = (filterConfigs: FilterConfig[]): Set<string> =>
  new Set([
    ...STRING_OPERATOR_FIELDS,
    ...filterConfigs.filter((config) => config.inputType === FilterInputType.StringOperator).map((config) => config.id),
  ]);

const toFilterId = (item: string | DropdownOptionFilter): string => (typeof item === 'string' ? item : item.value);

const isStringOperatorFilterShape = (value: unknown): value is { operator: string; values: string[] } =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && 'values' in value;

// Nested (StringOperatorFilter) wins, falling back to a legacy flat array — same pattern used for
// range filters in KATANA-1277. Tolerant of both: a raw TraffickingFilters response from a
// shareable link / custom filter (StringOperatorFilter shape) and GlobalFilters previously written
// to session storage (flat array, from before this migration or after down-conversion here).
//
// No STRING_OPERATOR_FIELDS/filterConfigs gate: relies solely on isStringOperatorFilterShape's
// runtime detection, which already distinguishes {operator, values} from DateOperatorFilter,
// RangeFilter, and DropdownOptionFilter[]. This covers agencyIdList/industryIdList for free — their
// read/output shape (TraffickingFilters.agencyIdList) is EntityOperatorFilter
// (`{operator, values: DropdownOptionFilter[]}`), which matches the shape check same as the 15
// StringOperatorFilter fields, so `.values` (labels intact) is extracted the same way — and it
// naturally covers future config-only fields with no MC-side edit.
const unwrapStringOperatorField = (value: unknown): unknown =>
  isStringOperatorFilterShape(value) ? value.values : value;

// KATANA-1499 follow-up: the actual {operator, values} construction, factored out so callers that
// already know (from a FilterConfig's inputType) that an id should be wrapped can call it directly
// without going through the key/Set check below — see getConfigDrivenFilterVars/
// buildCustomFilterPayload in pages/Trafficking/utils.ts and CustomSavedFilters/utils.ts.
export const wrapAsStringOperatorFilter = (value: unknown): unknown =>
  Array.isArray(value)
    ? { operator: StringOperator.Contains, values: (value as (string | DropdownOptionFilter)[]).map(toFilterId) }
    : value;

export const wrapSaveStringOperatorField = (
  key: string,
  value: unknown,
  stringOperatorKeys: Set<string> = STRING_OPERATOR_FIELDS
): unknown => (stringOperatorKeys.has(key) ? wrapAsStringOperatorFilter(value) : value);

// Canonical home for this guard/translator (see the STRING_OPERATOR_FIELDS comment above for why
// this file, not pages/Trafficking/*, owns shared filter-shape helpers) — operatorQueryVariables.ts
// (multiSelectToGlobalFilters) and FilterPanel.tsx (hydrateFilterValue) both import from here.
export const isOperatorFilterValue = (value: unknown): value is OperatorFilterValue =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  'operator' in value &&
  'values' in value &&
  Array.isArray((value as { values: unknown }).values);

// Mirrors campaign-searcher's DateOperatorValidator. Lenient for THIS, whose value is null by
// design — requiring `value != null` here would silently drop every "This <unit>" filter.
export const isRelativeDateFilterFilled = (filter: {
  direction?: Maybe<string>;
  unit?: Maybe<string>;
  referenceDate?: Maybe<string>;
  value?: Maybe<number>;
}): boolean => {
  const { direction, unit, referenceDate, value } = filter;
  if (!direction || !unit || !referenceDate) return false;
  if (direction === RelativeDirection.This) return true;
  return value != null && value > 0;
};

// Matches any date filter carrying a conditional (relative) boundary — IS_RELATIVE_TO_TODAY and
// comparison operators (IS_BEFORE, IS_AFTER, IS_ON_OR_BEFORE, IS_ON_OR_AFTER) with a non-null
// direction. The direction field is the discriminant: fixed-date filters have direction === null.
const hasConditionalBoundary = (value: unknown): value is { operator: string; direction: string } =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  typeof (value as { direction?: unknown }).direction === 'string';

// Normalizes an inbound relative filter: clamps `value` to the UI's bounds (nulling it for THIS,
// which DateOperatorValidator throws on), re-anchors `referenceDate` to today-local (derived state —
// a filter saved in January must still mean "next 7 days from today"), and drops a co-stored
// calendar `date` when a conditional triple is present (KATANA-1751 AC #4). Runs here because
// getDefaultFilters is the chokepoint every untrusted ingress (session storage, shareable link,
// saved filter) funnels through, and hydration is not on the query path.
const normalizeRelativeDateFilter = (value: unknown): unknown => {
  if (!hasConditionalBoundary(value)) return value;

  const filter = value as {
    direction?: Maybe<string>;
    value?: Maybe<number>;
    referenceDate?: Maybe<string>;
    date?: Maybe<string>;
  };
  const clamped =
    filter.direction === RelativeDirection.This ? null : clampRelativeValue(filter.value as number | null | undefined);
  const today = todayLocalIso();
  // Conditional triple wins — drop a co-stored calendar date.
  const dropDate = filter.date != null;

  // Preserve identity when unchanged, so downstream referential-equality deps don't refetch.
  if (clamped === (filter.value ?? null) && filter.referenceDate === today && !dropDate) return value;

  return { ...(value as object), value: clamped, referenceDate: today, ...(dropDate ? { date: null } : {}) };
};

/**
 * KATANA-1509: converts the legacy paired brandIdList/excludeBrandIdList values into the
 * canonical `{ operator, values }` shape, for backward compatibility with shareable links and
 * saved filters persisted before the brandIdOperator migration. Whichever key is populated wins;
 * include wins if both happen to be populated (matches the pre-migration hydration logic this
 * replaces). Returns null when neither key has a value, so callers can leave the canonical field
 * untouched rather than overwrite it with an empty filter.
 */
export const translateLegacyBrandOperator = (includeVal: unknown, excludeVal: unknown): OperatorFilterValue | null => {
  const includeValues = Array.isArray(includeVal) ? (includeVal as DropdownOptionFilter[]) : [];
  const excludeValues = Array.isArray(excludeVal) ? (excludeVal as DropdownOptionFilter[]) : [];
  if (includeValues.length === 0 && excludeValues.length === 0) return null;

  const isExclude = includeValues.length === 0 && excludeValues.length > 0;
  return {
    operator: isExclude ? FilterOperator.DoesNotContain : FilterOperator.Contains,
    values: isExclude ? excludeValues : includeValues,
  };
};

export const getDefaultFilters = (
  storageFilter: Partial<TraffickingFilters> | PersistedFilters | undefined = filtersData.getAllStorageData()
): GlobalFilters => {
  if (!storageFilter) {
    return INITIAL_FILTERS as GlobalFilters;
  }

  const storageFilterRecord = storageFilter as Record<string, unknown>;

  const processedFilters = (Object.keys(INITIAL_FILTERS) as Array<keyof GlobalFilters>).reduce((acc, filterName) => {
    if (isTraffickingTableFilter(filterName)) {
      return { ...acc, [filterName]: mergeTableFilters(filterName, storageFilterRecord[filterName] as never) };
    }

    if (isTraffickingLineItemStatusFilter(filterName)) {
      return {
        ...acc,
        ...mergeLineItemStatusFilters(
          storageFilterRecord.lineItemStatusList as string[] | undefined,
          storageFilterRecord.lineItemSubStatusList as string[] | undefined
        ),
      };
    } else if (isTraffickingDefaultFilter(filterName)) {
      return { ...acc, [filterName]: storageFilterRecord[filterName] || INITIAL_FILTERS[filterName] };
    }

    return {
      ...acc,
      [filterName]: normalizeRelativeDateFilter(unwrapStringOperatorField(storageFilterRecord[filterName])),
    };
  }, {} as GlobalFilters);

  // Pass through V2 filter panel config-driven fields from storageFilter that are not registered
  // in INITIAL_FILTERS (e.g. maxDuration, campaignStatusList). Without this, those fields are
  // silently dropped when a shareable link is loaded and getDefaultFilters merges the GQL response.
  //
  // Excluded: lineItemSubStatusList is consumed by mergeLineItemStatusFilters above and must not
  // appear directly in GlobalFilters; __typename is an Apollo artifact, not a filter field.
  const INTERNAL_ONLY_KEYS = new Set<string>(['lineItemSubStatusList', '__typename']);

  const extraKeys = Object.keys(storageFilterRecord).filter(
    (key) => !(key in INITIAL_FILTERS) && !INTERNAL_ONLY_KEYS.has(key)
  );

  // The relative-capable calendar keys are absent from INITIAL_FILTERS, so they arrive here rather
  // than via the reduce above — both paths need normalizing.
  const merged = extraKeys.reduce(
    (acc, key) => ({ ...acc, [key]: normalizeRelativeDateFilter(storageFilterRecord[key]) }),
    processedFilters
  );

  // KATANA-1509: Brand's wire key changed (brandIdList/excludeBrandIdList -> brandIdOperator) —
  // unlike e.g. lineItemEndDate (KATANA-1188), whose key stayed put and only the value shape
  // changed. A pre-migration shareable link or saved custom filter has brandIdOperator absent but
  // a populated legacy pair; synthesize the canonical field here (in-memory only — nothing is
  // written back unless the user re-saves) so getAllowedFilterKeys/getConfigDrivenFilterVars,
  // which with the V2 flag on only forward brandIdOperator, still filter correctly (AC #3).
  // campaign-graphql's buildBrandStringOperatorField legacy inference remains a safety net for
  // callers that bypass this boundary (e.g. a direct live-query call with a stale payload).
  const legacyBrandOperator = isOperatorFilterValue(merged.brandIdOperator)
    ? null
    : translateLegacyBrandOperator(merged.brandIdList, merged.excludeBrandIdList);

  // Temporary bridge until V1 filter code is removed: when a V2 key is populated, suppress the
  // corresponding V1 key so that clearing V2 (e.g. via FilterPanel "Clear All") leaves no stale
  // V1 value visible in the TableFilterBar's `v2 ?? v1` fallback display.
  return {
    ...merged,
    accountManager: merged.traffickerEmailList?.length ? null : merged.accountManager,
    accountExecutive: merged.accountExecutiveList?.length ? null : merged.accountExecutive,
    productType: merged.adProductList?.length ? null : merged.productType,
    ...(legacyBrandOperator
      ? { brandIdOperator: legacyBrandOperator, brandIdList: null, excludeBrandIdList: null }
      : {}),
  };
};

const isTraffickingTableFilterParam = (key: string): boolean =>
  ([
    TRAFFICKING_SEARCH_PARAMS.LINE_ITEMS_SELECTED_IDS,
    TRAFFICKING_SEARCH_PARAMS.ADS_ITEMS_SELECTED_IDS,
  ] as string[]).includes(key);

const getTraffickingTableFiltersFromSearchParams = (
  key: string,
  params: URLSearchParams
): TraffickingFilters[TraffickingTableName] => {
  const selectedRowIds = (params.get(key)?.split(',') || []).filter(Boolean);

  return {
    selectedRowIds,
    sortBy: null,
    searchTerm: '',
  };
};

const getTraffickingFiltersFromSearchParams = (params: URLSearchParams): Partial<TraffickingFilters> => {
  let filters: Partial<TraffickingFilters> = {};

  params.forEach((value, key) => {
    if (isTraffickingTableFilterParam(key)) {
      const tableName: TraffickingTableName =
        key === TRAFFICKING_SEARCH_PARAMS.LINE_ITEMS_SELECTED_IDS
          ? TraffickingTableName.lineItems
          : TraffickingTableName.ads;

      filters = { ...filters, [tableName]: getTraffickingTableFiltersFromSearchParams(key, params) };
    }

    if (key === TRAFFICKING_SEARCH_PARAMS.SELECTED_TAB) {
      const selectedTab = (TRAFFICKING_TABLE_NAMES as string[]).includes(value)
        ? value
        : INITIAL_FILTERS[TRAFFICKING_SEARCH_PARAMS.SELECTED_TAB];

      filters = { ...filters, [key]: selectedTab };
    }

    if (key === TRAFFICKING_SEARCH_PARAMS.DATE_FILTER && value === ALL_TIME_DATE_KEY) {
      filters = { ...filters, [key]: { label: ALL_TIME_DATE_KEY, value: null } };
    }
  });

  return filters;
};

export const hasFiltersParams = (params: URLSearchParams): boolean =>
  TRAFFICKING_FILTERS_SEARCH_PARAMS.some((param) => params.has(param));

export const getInitialFilters = (shareableId: Nullable<string>, params: URLSearchParams): GlobalFilters => {
  if (hasFiltersParams(params)) return getDefaultFilters(getTraffickingFiltersFromSearchParams(params));

  if (!shareableId) return getDefaultFilters();

  return INITIAL_FILTERS as GlobalFilters;
};

export const getFiltersToApplyByCustomFilter = (
  customFilter: Nullable<DropdownOption<TraffickingFilters>>,
  filters: GlobalFilters
): GlobalFilters => {
  // Preserve only genuinely non-panel state (selected tab, date, and per-table selection/search/sort)
  // from the current filters when applying a saved filter. Alert booleans come from the selected
  // filter's saved payload (KATANA-1513).
  const tableFilters: OmittedCustomFiltersFields = {
    [TraffickingTableName.campaigns]: filters.campaigns,
    [TraffickingTableName.lineItems]: filters.lineItems,
    [TraffickingTableName.ads]: filters.ads,
    selectedTab: filters.selectedTab,
    dateFilter: filters.dateFilter,
  };
  let storageFilter: Partial<TraffickingFilters> = customFilter?.value
    ? removeTypeNamesFromObject<TraffickingFilters>({ ...customFilter.value, ...tableFilters })
    : tableFilters;

  if (customFilter?.id === PredefinedCustomFilterId.CUSTOM_PACING_ELIGIBLE) {
    const overriddenFields: OverriddenCustomPacingEligibleFields = {
      dateFilter: { label: ALL_TIME_DATE_KEY, value: null },
    };

    storageFilter = { ...storageFilter, ...overriddenFields };
  }

  return getDefaultFilters(storageFilter);
};

export const setFilterDataToStorage = (filter: keyof GlobalFilters, value: unknown): void => {
  if (filter === TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_STATUS_LIST) {
    const statusInput = getLineItemStatusFiltersInput(Array.isArray(value) ? value : null);
    const { lineItemStatusList, lineItemSubStatusList } = statusInput;

    filtersData.setStorageData('lineItemStatusList', lineItemStatusList);
    filtersData.setStorageData('lineItemSubStatusList', lineItemSubStatusList);
  } else {
    filtersData.setStorageData(filter, value as Partial<GlobalFilters>[keyof GlobalFilters]);
  }
};
